import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faLock, faShield, faTag, faCreditCard, faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import { Elements } from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';
import CartSummary from '../components/CartSummary';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { createCheckoutSession, getStripe } from '../utils/stripe';
import { getStripeElements, createPaymentIntent, calculateOrderTotal } from '../utils/stripeElements';
import StripeCheckoutForm from '../components/StripeCheckoutForm';

const Checkout: React.FC = () => {
  const { cart } = useCart();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [shippingCountry, setShippingCountry] = useState('US');
  const [useElements, setUseElements] = useState(true); // Toggle between Elements and Checkout
  const [clientSecret, setClientSecret] = useState<string>('');
  const [stripePromise] = useState(() => getStripeElements());

  // If cart is empty, redirect to cart page
  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/cart');
    }
  }, [cart.items.length, navigate]);

  // Initialize Payment Intent for Elements
  useEffect(() => {
    if (useElements && cart.items.length > 0) {
      initializePaymentIntent();
    }
  }, [useElements, cart.items.length]);

  const initializePaymentIntent = async () => {
    try {
      setIsLoading(true);
      const customerData = email ? { email } : undefined;
      const paymentData = await createPaymentIntent(cart, customerData);
      setClientSecret(paymentData.client_secret);
    } catch (err: any) {
      console.error('Failed to create payment intent:', err);
      setError(err.message || 'Failed to initialize payment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (cart.items.length === 0) {
      setError('Your cart is empty');
      return;
    }

    if (!email) {
      setError('Please provide your email');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Add user data to checkout
      const userData = {
        email: email,
        userId: localStorage.getItem('userId') || '',
      };

      // Create checkout session
      const session = await createCheckoutSession(cart);

      // Redirect to Stripe checkout
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      const { error } = await stripe.redirectToCheckout({
        sessionId: session.id
      });

      if (error) {
        throw error;
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      setError(err.message || 'Something went wrong with checkout');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-dark text-primary">
      <Header />
      
      <main className="py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link to="/cart" className="text-accent hover:text-accent/80 flex items-center">
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            <span>Back to Cart</span>
          </Link>
        </div>
        
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Checkout</h1>
          
          {/* Toggle between Elements and Checkout Sessions */}
          <div className="flex items-center space-x-3">
            <span className="text-sm text-secondary">Embedded Form</span>
            <button
              onClick={() => setUseElements(!useElements)}
              className="focus:outline-none"
            >
              <FontAwesomeIcon 
                icon={useElements ? faToggleOn : faToggleOff} 
                className={`h-6 w-6 ${useElements ? 'text-accent' : 'text-gray-400'}`}
              />
            </button>
            <span className="text-sm text-secondary">Hosted Page</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main checkout content */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Error message */}
            {error && (
              <div className="bg-red-900/20 text-red-400 p-4 rounded-lg">
                {error}
              </div>
            )}
            
            {useElements ? (
              // Stripe Elements Integration
              clientSecret && stripePromise ? (
                <Elements 
                  stripe={stripePromise} 
                  options={{ 
                    clientSecret,
                    appearance: {
                      theme: 'night',
                      variables: {
                        colorPrimary: '#8b5cf6',
                        colorBackground: '#1f2937',
                        colorText: '#f9fafb',
                        colorDanger: '#ef4444',
                        fontFamily: 'system-ui, sans-serif',
                        spacingUnit: '4px',
                        borderRadius: '8px',
                      }
                    }
                  }}
                >
                  <StripeCheckoutForm
                    onSuccess={() => navigate('/checkout/success')}
                    onError={setError}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                  />
                </Elements>
              ) : (
                <div className="bg-background-light rounded-lg shadow-md p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
                  <p className="text-secondary">Loading payment form...</p>
                </div>
              )
            ) : (
              // Original Checkout Session Flow
              <>
                {/* Checkout steps */}
                <div className="bg-background-light rounded-lg shadow-md p-6">
                  <h2 className="text-lg font-bold mb-4">Contact Information</h2>
                  
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-3 border border-background-dark/20 rounded-md focus:ring-2 focus:ring-accent/50 focus:border-accent bg-background-dark/5"
                      placeholder="your@email.com"
                      required
                    />
                    <p className="text-xs text-secondary mt-1">
                      We'll send your receipt and order updates to this email
                    </p>
                  </div>
                </div>
                
                <div className="bg-background-light rounded-lg shadow-md p-6">
                  <h2 className="text-lg font-bold mb-4">Shipping</h2>
                  <p className="text-secondary mb-4">
                    Shipping and delivery details will be collected during Stripe checkout.
                  </p>
                  
                  <div className="mb-4">
                    <label htmlFor="country" className="block text-sm font-medium mb-1">
                      Country
                    </label>
                    <select
                      id="country"
                      value={shippingCountry}
                      onChange={(e) => setShippingCountry(e.target.value)}
                      className="w-full p-3 border border-background-dark/20 rounded-md focus:ring-2 focus:ring-accent/50 focus:border-accent bg-background-dark/5"
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                    </select>
                  </div>
                </div>
                
                <div className="bg-background-light rounded-lg shadow-md p-6">
                  <h2 className="text-lg font-bold mb-4">Payment</h2>
                  <p className="text-secondary">
                    Your payment information will be collected securely by Stripe.
                  </p>
                  
                  <div className="flex items-center space-x-2 mt-4">
                    <FontAwesomeIcon icon={faLock} className="text-accent" />
                    <span className="text-sm text-secondary">Secure payment processing by Stripe</span>
                  </div>
                  
                  <div className="mt-6 flex flex-wrap gap-2">
                    <div className="bg-gray-200 rounded px-2 py-1">
                      <img src="/payment-icons/visa.svg" alt="Visa" className="h-6" />
                    </div>
                    <div className="bg-gray-200 rounded px-2 py-1">
                      <img src="/payment-icons/mastercard.svg" alt="Mastercard" className="h-6" />
                    </div>
                    <div className="bg-gray-200 rounded px-2 py-1">
                      <img src="/payment-icons/amex.svg" alt="American Express" className="h-6" />
                    </div>
                    <div className="bg-gray-200 rounded px-2 py-1">
                      <img src="/payment-icons/applepay.svg" alt="Apple Pay" className="h-6" />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Order summary */}
          <div>
            <CartSummary 
              cart={cart}
              showCheckoutButton={!useElements} // Only show checkout button for hosted flow
              isLoading={isLoading}
              onCheckout={handleCheckout}
            />
            
            {/* Trust badges */}
            <div className="mt-6 bg-background-light rounded-lg shadow-md p-4">
              <h3 className="text-sm font-semibold mb-3">Shop With Confidence</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faShield} className="text-accent mr-2" />
                  <span className="text-xs">Secure Shopping</span>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faTag} className="text-accent mr-2" />
                  <span className="text-xs">100% Authentic</span>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faCreditCard} className="text-accent mr-2" />
                  <span className="text-xs">Secure Payment</span>
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faLock} className="text-accent mr-2" />
                  <span className="text-xs">Privacy Protected</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Checkout; 